import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

/**
 * Top-level provider selection for accounts that own more than one business.
 *
 * The selection is held in component state and mirrored to sessionStorage so
 * page reloads inside the same tab preserve the choice. Logging out or opening
 * a new tab starts fresh (the gate re-prompts when needed).
 *
 * Consumers read both the selected provider object and the loaded list, and
 * call setSelectedProviderId to switch. The BusinessProfileGate component
 * below is the one place that enforces a selection has been made before
 * rendering the profile UI.
 */

const STORAGE_KEY = "selectedProviderId";

const SelectedProviderContext = createContext({
  providers: [],
  selectedProvider: null,
  selectedProviderId: null,
  setSelectedProviderId: () => {},
  refreshProviders: () => Promise.resolve([]),
  loading: false,
  error: null,
});

export function SelectedProviderProvider({ children, fetchProviders }) {
  const [providers, setProviders] = useState([]);
  const [selectedProviderId, _setSelectedProviderId] = useState(() => {
    try {
      return sessionStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refreshProviders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await fetchProviders();
      const arr = Array.isArray(list) ? list : [];
      setProviders(arr);

      // Auto-select when the user owns exactly one provider — no need to
      // prompt them.
      if (arr.length === 1) {
        const onlyId = arr[0].id;
        _setSelectedProviderId(onlyId);
        try { sessionStorage.setItem(STORAGE_KEY, onlyId); } catch {}
      } else if (arr.length === 0) {
        _setSelectedProviderId(null);
        try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
      } else {
        // Multi-provider account. Validate the persisted selection still
        // exists in the latest list; clear it otherwise so the gate prompts.
        _setSelectedProviderId((current) => {
          const stillValid = current && arr.some((p) => p.id === current);
          if (!stillValid) {
            try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
            return null;
          }
          return current;
        });
      }
      return arr;
    } catch (e) {
      setError(e);
      setProviders([]);
      _setSelectedProviderId(null);
      return [];
    } finally {
      setLoading(false);
    }
  }, [fetchProviders]);

  useEffect(() => {
    refreshProviders();
  }, [refreshProviders]);

  const setSelectedProviderId = useCallback((id) => {
    _setSelectedProviderId(id);
    try {
      if (id) sessionStorage.setItem(STORAGE_KEY, id);
      else sessionStorage.removeItem(STORAGE_KEY);
    } catch {}
  }, []);

  const selectedProvider =
    providers.find((p) => p.id === selectedProviderId) || null;

  return (
    <SelectedProviderContext.Provider
      value={{
        providers,
        selectedProvider,
        selectedProviderId,
        setSelectedProviderId,
        refreshProviders,
        loading,
        error,
      }}
    >
      {children}
    </SelectedProviderContext.Provider>
  );
}

export function useSelectedProvider() {
  return useContext(SelectedProviderContext);
}
